import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Archive, RotateCcw, Pencil, ChevronUp, ChevronDown } from 'lucide-react';
import { PageHeader } from '../common/ui';
import { Project } from '../../types/time';
import TimeNav from './TimeNav';

const CATEGORY_LABELS: Record<string, string> = {
  production: 'Production',
  repair: 'Repair',
  other: 'Other'
};

const CATEGORY_COLORS: Record<string, string> = {
  production: 'bg-blue-100 text-blue-700',
  repair: 'bg-purple-100 text-purple-700',
  other: 'bg-gray-100 text-gray-600'
};

const PRESET_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6'
];

const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#3B82F6', category: 'other' });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/projects');
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`/api/projects/${editingProject.id}`, formData);
      } else {
        await axios.post('/api/projects', formData);
      }
      setShowModal(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', color: '#3B82F6', category: 'other' });
      fetchProjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save project');
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({ name: project.name, description: project.description || '', color: project.color, category: project.category || 'other' });
    setShowModal(true);
  };

  const handleToggleArchive = async (id: number) => {
    try {
      await axios.patch(`/api/projects/${id}/archive`);
      fetchProjects();
    } catch (err) {
      console.error('Error toggling archive:', err);
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const activeProjects = projects.filter(p => p.status === 'active');
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= activeProjects.length) return;

    const newOrder = [...activeProjects];
    [newOrder[index], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[index]];

    const orderPayload = newOrder.map((p, i) => ({ id: p.id, sort_order: i }));
    try {
      await axios.put('/api/projects/reorder', { order: orderPayload });
      fetchProjects();
    } catch (err) {
      console.error('Error reordering:', err);
    }
  };

  const filtered = showArchived ? projects : projects.filter(p => p.status === 'active');

  return (
    <div className="p-6">
      <TimeNav />

      <div className="flex justify-between items-center mb-6">
        <PageHeader title="Projects" subtitle="" />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 text-naviter-blue border-gray-300 rounded"
            />
            Show archived
          </label>
          <button
            onClick={() => {
              setEditingProject(null);
              setFormData({ name: '', description: '', color: '#3B82F6', category: 'other' });
              setShowModal(true);
            }}
            className="bg-naviter-blue hover:bg-naviter-blue-dark text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
          >
            <Plus size={16} />
            New Project
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No projects found. Create your first project!</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
          <div className="px-4 py-2 bg-gray-50 rounded-t-lg text-xs font-medium text-gray-500 uppercase flex items-center">
            <span className="w-8 text-center">#</span>
            <span className="w-10"></span>
            <span className="flex-1 ml-3">Project</span>
            <span className="w-32 text-right">Actions</span>
          </div>
          {filtered.map((project, index) => {
            const isActive = project.status === 'active';
            const activeList = projects.filter(p => p.status === 'active');
            const activeIndex = activeList.findIndex(p => p.id === project.id);
            return (
              <div
                key={project.id}
                className={`flex items-center px-4 py-3 hover:bg-gray-50 ${!isActive ? 'opacity-50' : ''}`}
              >
                <span className="w-8 text-center text-sm font-medium text-gray-400">
                  {isActive ? activeIndex + 1 : '—'}
                </span>

                <div className="w-10 flex flex-col items-center">
                  {isActive && (
                    <>
                      <button
                        onClick={() => handleMove(activeIndex, 'up')}
                        disabled={activeIndex === 0}
                        className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-default"
                        title="Move up"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        onClick={() => handleMove(activeIndex, 'down')}
                        disabled={activeIndex === activeList.length - 1}
                        className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-default"
                        title="Move down"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </>
                  )}
                </div>

                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 ml-3"
                  style={{ backgroundColor: project.color }}
                />

                <div className="flex-1 min-w-0 ml-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{project.name}</h3>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${CATEGORY_COLORS[project.category] || CATEGORY_COLORS.other}`}>
                      {CATEGORY_LABELS[project.category] || 'Other'}
                    </span>
                  </div>
                  {project.description && (
                    <p className="text-xs text-gray-500 truncate">{project.description}</p>
                  )}
                </div>

                {!isActive && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full mr-2">Archived</span>
                )}

                <div className="w-32 flex items-center justify-end gap-1">
                  <button
                    onClick={() => handleEdit(project)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                    title="Edit"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => handleToggleArchive(project.id)}
                    className="p-1.5 text-gray-400 hover:text-amber-600 rounded"
                    title={project.status === 'archived' ? 'Activate' : 'Archive'}
                  >
                    {project.status === 'archived' ? <RotateCcw size={15} /> : <Archive size={15} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingProject ? 'Edit Project' : 'New Project'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="Project name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                >
                  <option value="production">Production</option>
                  <option value="repair">Repair</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === c ? 'border-gray-900 scale-110' : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-300"
                    title="Custom color"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingProject(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark text-sm"
                >
                  {editingProject ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList;
